import React, {useEffect, useState, useMemo, useCallback} from "react";
import {inject, observer} from "mobx-react";
import cn from "classnames";
import {debounce} from "lodash";
import {v4 as uuidv4} from "uuid";
// local
import {useTranslation} from "react-i18next";
import {ConfirmModal} from "components/ConfirmModal";
import DocumentRevisionTabs from "components/requestCard/DocumentRevisionTabs";
import {TitleIcon} from "components/TitleIcon";
import LoadDocsColumn from "components/LoadDocsColumn";
import SelectDocColumn from "components/selects/SelectDocColumn";
import AddLinkModal from "components/modals/AddLinkModal/AddLinkModal";
import {docType} from "constants/file.consts";
import {saveDocument, removeFilesConfirmModal} from "helpers/helper";
import {IconDocumentPen, IconRules, IconLink, IconBriefcase} from "components/icons";
import AddDaluxLinkModal from "components/modals/AddDaluxLinkModal";
import {showWarning} from "helpers/notifications.helper";

const RequestDocuments = inject("store")(
  observer(
    ({
      store: {buyer},
      revisions,
      editable,
      needZipButton,
      disableZipButton,
      columnTitles,
      allRevisionsView,
      showRevisionNameInColumn,
      customLoadDocument,
      showTabs,
      intoContract,
      standardAgreement,
      sellerSide,
      publicView,
    }) => {
      const {isAddLinkModal, isAddDaluxLinkModal} = buyer;
      const {t} = useTranslation();
      const [loading, setLoading] = useState(false);
      const [selectItems, setSelectItems] = useState([]);
      const [loadColumnTitle, setLoadColumnTitle] = useState("");

      const isShowTooltip = useMemo(
        () => showTabs && editable && revisions?.length < 2,
        [showTabs, revisions?.length, editable]
      );
      const isContinuousFileList = useMemo(
        () => allRevisionsView && !showRevisionNameInColumn,
        [allRevisionsView, showRevisionNameInColumn]
      );

      // REVISIONS
      const [revisionId, setRevisionId] = useState(revisions?.[0]?.id);
      const revision = useMemo(
        () => revisions?.find((item) => item.id === revisionId),
        [revisions, revisionId]
      );
      const documents = useMemo(
        () => ({
          contracts: revision?.contracts ?? [],
          documents: revision?.documents ?? [],
          plans: revision?.plans ?? [],
          links: revision?.links ?? [],
          id: revision?.id,
        }),
        [revision]
      );

      const [currentColumnRevisionFiles, setCurrentColumnRevisionFiles] =
        useState(documents);

      const convertUrlToNames = (links) =>
        links.map((link) => ({
          ...link,
          name: link.url?.includes("daluxFile") ? link.url.split("/")[1] : link.url,
        }));

      const divideDaluxFilesIntoFolders = useCallback((links) => {
        const foldersWithFiles = links.reduce((list, link) => {
          const folderId = link.url.split("/")[6];
          const existingFolder = list.find((folder) => folder.id === folderId);
          return existingFolder
            ? list.map((folder) =>
                folder.id === folderId
                  ? {...folder, files: [...folder.files, link]}
                  : folder
              )
            : [...list, {id: folderId, name: link.url.split("/")[7], files: [link]}];
        }, []);
        const sortedFilesInFolders = foldersWithFiles.map((folder) => ({
          ...folder,
          files: folder.files.collatorSortObj("name"),
        }));
        const sortedFolders = sortedFilesInFolders.collatorSortObj("name");
        const flattedFolderFiles = sortedFolders.reduce(
          (list, folder) => [
            ...list,
            {id: folder.id, folderName: folder.name},
            ...(folder.files ?? []),
          ],
          []
        );
        return flattedFolderFiles;
      }, []);

      const prepareLinksList = useCallback(
        (links) => {
          const namedLinks = convertUrlToNames(links);
          const directLinks = namedLinks
            .filter((link) => !link.url?.includes("daluxFile"))
            .collatorSortObj("name");
          const daluxFilesLinks = namedLinks.filter((link) =>
            link.url?.includes("daluxFile")
          );
          const daluxFilesLinksWFolders = daluxFilesLinks.filter(
            (link) => link.url.split("/").length > 6
          );
          const dividedDaluxFileLinksIntoFolders = divideDaluxFilesIntoFolders(
            daluxFilesLinksWFolders
          );
          const daluxFileLiknsWOFolders = daluxFilesLinks
            .filter((link) => link.url.split("/").length < 6)
            .collatorSortObj("name");

          return isContinuousFileList
            ? [...daluxFilesLinks, ...directLinks]
            : [
                ...dividedDaluxFileLinksIntoFolders,
                ...daluxFileLiknsWOFolders,
                ...directLinks,
              ];
        },
        [divideDaluxFilesIntoFolders, isContinuousFileList]
      );

      const collectDocuments = useCallback(
        (key) =>
          revisions
            ?.reduce(
              (docs, item) =>
                docs.concat(
                  item[key].length
                    ? showRevisionNameInColumn
                      ? [
                          {id: item.id, revisionName: item.name},
                          ...(key === "links" ? prepareLinksList(item[key]) : item[key]),
                        ]
                      : key === "links"
                      ? prepareLinksList(item[key])
                      : item[key]
                    : []
                ),
              []
            )
            [showRevisionNameInColumn ? "noop" : "collatorSortObj"]("name"),
        [revisions, showRevisionNameInColumn, prepareLinksList]
      );

      const allDocuments = useMemo(
        () => ({
          contracts: collectDocuments("contracts"),
          documents: collectDocuments("documents"),
          plans: collectDocuments("plans"),
          links: collectDocuments("links"),
        }),
        [collectDocuments]
      );
      const files = useMemo(
        () => ({
          contracts: allRevisionsView
            ? allDocuments.contracts ?? []
            : documents.contracts ?? [],
          documents: allRevisionsView
            ? allDocuments.documents ?? []
            : documents.documents ?? [],
          plans: allRevisionsView ? allDocuments.plans ?? [] : documents.plans ?? [],
          links: allRevisionsView ? allDocuments.links ?? [] : documents.links ?? [],
        }),
        [allDocuments, documents, allRevisionsView]
      );

      // const isDaluxZipButton = useMemo(
      //   () =>
      //     sellerSide &&
      //     files.links.filter((item) => item.url.includes("daluxFile")).length > 1,
      //   [sellerSide, files]
      // );

      const onChangeSelect = debounce(
        (value) => buyer.updateCurrentRequestInfo({standardAgreement: value}),
        2000
      );

      const contractType = (
        <SelectDocColumn
          label={"STANDARD_AGREEMENT"}
          className="mb-0 mt-6"
          elements={selectItems}
          selected={standardAgreement}
          editable={editable}
          onChangeSelect={onChangeSelect}
        />
      );

      const handleGetContractRevision = useCallback(() => {
        const contractRevision = revisions?.find(
          (item) => item.name === "RFP_FOR_CONTRACT_REVISION_NAME"
        );
        const newContractRevisionId = uuidv4();
        setRevisionId(contractRevision?.id ?? newContractRevisionId);
        const contractRevisionDocuments = {
          contracts: [],
          documents: [],
          plans: [],
          links: [],
          id: contractRevision?.id ?? newContractRevisionId,
        };
        const newContractRevision = {
          id: newContractRevisionId,
          name: "RFP_FOR_CONTRACT_REVISION_NAME",
          ...contractRevisionDocuments,
        };
        intoContract &&
          !contractRevision?.id &&
          setCurrentColumnRevisionFiles(contractRevisionDocuments);
        return contractRevision ?? newContractRevision;
      }, [revisions, intoContract]);

      const handleAddEntity = (title, data) => {
        const currentRevision = intoContract ? handleGetContractRevision() : revision;
        setCurrentColumnRevisionFiles((prevState) => ({
          ...currentRevision,
          [docType[title]]: [
            ...(prevState.id === currentRevision.id
              ? prevState[docType[title]]
              : currentRevision[docType[title]]),
            ...(Array.isArray(data) ? data : [data]),
          ],
        }));
      };

      const clickAdd = (filesData, title) => {
        setLoadColumnTitle(title);
        setLoading(true);
        const uploadFiles = filesData.map((fileData) => buyer.getUID(fileData));
        Promise.all(uploadFiles)
          .then((results) => handleAddEntity(title, results))
          .finally(() => setLoading(false));
      };

      const handleAddLink = (url) =>
        files.links.some((link) => link.url === url)
          ? showWarning(t("CANT_ADD_LINK"))
          : handleAddEntity(columnTitles.links, {id: uuidv4(), url});
      const handleAddDaluxLink = (urls) =>
        handleAddEntity(
          columnTitles.links,
          urls?.map((url) => ({...url, id: uuidv4()}))
        );

      const clickRemove = (id, title) =>
        removeFilesConfirmModal(id, (id) => removeDocument(id, title));
      const removeDocument = (id, title, idKey = "id") => {
        const columnRevisionFiles = {
          ...revision,
          [docType[title]]: id // id === null, if we click remove all button
            ? revision[docType[title]].filter((val) => val[idKey] !== id)
            : [],
        };
        const payloadRevisions = revisions.map((item) =>
          (
            allRevisionsView
              ? item[docType[title]]?.find((file) => file.id === id)
              : item.id === revisionId
          )
            ? {
                ...item,
                [docType[title]]: id // id === null, if we click remove all button
                  ? item[docType[title]].filter((val) => val.id !== id)
                  : [],
              }
            : item
        );

        setCurrentColumnRevisionFiles(columnRevisionFiles);
        buyer.updateCurrentRequestInfo({
          documents: payloadRevisions,
        });
      };

      const removeLink = (id, title) =>
        id
          ? removeDocument(id, title)
          : ConfirmModal({
              title: t("WANT_DELETE_ALL_LINKS"),
              text: t("CANT_DELETE_ALL_LINKS"),
              type: "warning",
              onOk: () => removeDocument(id, title),
            });

      const loadDaluxFile = async (url) => {
        const urlData = url.split("/");
        let fileName = urlData[1];
        const data = await buyer.getDaluxProjectFileAreaFile(
          urlData[2],
          urlData[3],
          urlData[4],
          revisionId
        );
        if (!data) return;
        saveDocument(data, "application/octet-stream", fileName, t("FILE_DOWNLOADED"));
      };

      useEffect(() => {
        buyer
          .getAgreements()
          .then((response) =>
            setSelectItems([...response.data, "NO_STANDARD_AGREEMENT"])
          );
      }, [buyer]);

      useEffect(
        () => {
          const currentRevision = intoContract ? handleGetContractRevision() : revision;
          buyer.updateCurrentRequestInfo({
            documents: [
              ...(revisions?.filter((item) => item.id !== currentRevision.id) ?? []),
              {
                ...currentRevision,
                ...(currentRevision?.id === currentColumnRevisionFiles.id
                  ? currentColumnRevisionFiles
                  : {}),
              },
            ],
          });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentColumnRevisionFiles]
      );

      return (
        <React.Fragment>
          {!allRevisionsView && (
            <TitleIcon
              title={t("RFP_REQUEST_TAB_DOCUMENTS_SECTION_TITLE")}
              addClass="mt-8"
              icon={<IconBriefcase />}
              tooltip={
                isShowTooltip
                  ? "RFP_REQUEST_TAB_DOCUMENTS_SECTION_TITLE_TOOLTIP"
                  : undefined
              }
              prefixTooltipContent={
                showTabs ? (
                  <DocumentRevisionTabs
                    revisions={revisions}
                    revisionId={revisionId}
                    isEditing={editable}
                    onChangeTab={setRevisionId}
                  />
                ) : null
              }
            />
          )}
          <div className={cn("load-docs_wrapper")}>
            <LoadDocsColumn
              title={columnTitles.contracts}
              docPlaceholder="PLACEHOLDER_BUYER_CONTRACT_COLUMN"
              titleIcon={<IconDocumentPen />}
              button="ADD"
              editable={editable}
              docs={files.contracts}
              addFile={clickAdd}
              removeFile={clickRemove}
              canRemoveAllFiles
              needZipButton={needZipButton}
              disableZipButton={disableZipButton}
              loading={loading}
              allRevisionsView={allRevisionsView}
              loadColumnTitle={loadColumnTitle}
              bottomChildren={!publicView && contractType}
              customLoadDocument={customLoadDocument?.contracts}
            />
            <LoadDocsColumn
              title={columnTitles.documents}
              docPlaceholder="PLACEHOLDER_BUYER_DOCUMENT_COLUMN"
              titleIcon={<IconDocumentPen />}
              button="ADD"
              docs={files.documents}
              editable={editable}
              addFile={clickAdd}
              removeFile={clickRemove}
              canRemoveAllFiles
              needZipButton={needZipButton}
              loading={loading}
              allRevisionsView={allRevisionsView}
              loadColumnTitle={loadColumnTitle}
              customLoadDocument={customLoadDocument?.documents}
            />
            <LoadDocsColumn
              title={columnTitles.plans}
              docPlaceholder="PLACEHOLDER_BUYER_DOCUMENT_COLUMN"
              titleIcon={<IconRules />}
              button="ADD"
              docs={files.plans}
              editable={editable}
              addFile={clickAdd}
              removeFile={clickRemove}
              canRemoveAllFiles
              needZipButton={needZipButton}
              loading={loading}
              allRevisionsView={allRevisionsView}
              loadColumnTitle={loadColumnTitle}
              customLoadDocument={customLoadDocument?.plans}
            />
            <LoadDocsColumn
              title={columnTitles.links}
              docPlaceholder="PLACEHOLDER_BUYER_LINKS_COLUMN"
              titleIcon={<IconLink />}
              button="ADD"
              wide
              docs={
                allRevisionsView && showRevisionNameInColumn
                  ? files.links
                  : prepareLinksList(files.links)
              }
              editable={editable}
              removeFile={removeLink}
              canRemoveAllFiles
              loading={loading}
              allRevisionsView={allRevisionsView}
              customAdd={() => buyer.setAddLinkModalStatus(true)}
              linksView
              revisionId={revisionId}
              // needZipButton={needZipButton && isDaluxZipButton}
              customLoadDocument={loadDaluxFile}
              loadColumnTitle={loadColumnTitle}
              isOpenModal={isAddLinkModal}
              modal={<AddLinkModal addLink={handleAddLink} />}
            />
          </div>
          {isAddDaluxLinkModal && (
            <AddDaluxLinkModal revisionId={revisionId} addLink={handleAddDaluxLink} />
          )}
        </React.Fragment>
      );
    }
  )
);

export default RequestDocuments;
